import { useEffect, useRef } from "react";

/**
 * Compliancy Group HIPAA seal.
 *
 * The vendor's seal.js is a global side-effect script: when it loads, it
 * walks the DOM looking for the seal anchor and injects the badge image
 * into it. Two things make this fragile in a React app:
 *
 *  1. React 18 StrictMode double-invokes effects in dev. A naive
 *     "load the script once" effect ends up loading it twice and you
 *     get two seals stacked on top of each other.
 *  2. The script is global — once loaded, it stays loaded for the life
 *     of the page. A second `<script>` tag that points at the same URL
 *     re-runs the IIFE and re-injects the seal.
 *
 * Fix:
 *  - Track script-load state at the *module* level (survives StrictMode
 *    remounts and any number of <HipaaSealImage /> instances on a page).
 *  - Render the anchor + visually-hidden link as plain JSX; let seal.js
 *    find it the same way it does on the WP site.
 *  - Never inject <script> tags via innerHTML.
 */

const SEAL_KEY = "33fb246d-c7b1-41e4-b88f-6ed1325e2c37";
const SEAL_SCRIPT_ID = "hipaa-seal-script";
const SEAL_SCRIPT_SRC = `https://app-prod.compliancygroup.com/seal.js?key=${SEAL_KEY}`;

let sealScriptLoaded = false;

function ensureSealScriptLoaded(): void {
  if (sealScriptLoaded) return;
  if (document.getElementById(SEAL_SCRIPT_ID)) {
    sealScriptLoaded = true;
    return;
  }
  const script = document.createElement("script");
  script.id = SEAL_SCRIPT_ID;
  script.src = SEAL_SCRIPT_SRC;
  script.async = true;
  document.body.appendChild(script);
  sealScriptLoaded = true;
}

export default function HipaaSealImage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureSealScriptLoaded();
    // Intentionally no cleanup: the vendor script is a global, page-level
    // side effect. Removing the <script> tag wouldn't unload its listeners
    // anyway, and removing the seal node would just race the next mount.
  }, []);

  return (
    <div ref={containerRef} className="hipaa-seal">
      {/*
        seal.js looks for an anchor that points at the compliance dashboard
        and replaces / fills it with the badge image. Match the WP markup
        exactly so behavior is identical there and here.
      */}
      <a
        href="https://compliancy-group.com/compliance-monitoring-dashboard/"
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "block", width: 154 }}
      >
        <img
          src={`https://cg-seals-prod.s3.amazonaws.com/${SEAL_KEY}/fd2cd48404d2.png`}
          alt="HIPAA Compliancy Seal"
          style={{ maxWidth: "100%" }}
        />
      </a>
    </div>
  );
}